import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Menu, X, Crop, Globe, Search, Check, ChevronDown } from 'lucide-react'
import { useIntl } from 'react-intl'
import { useLanguage } from '../contexts/LanguageContext'

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'zh-cn', name: 'Chinese (Simplified)', nativeName: '简体中文' },
  { code: 'zh-tw', name: 'Chinese (Traditional)', nativeName: '繁體中文' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'fil', name: 'Filipino', nativeName: 'Filipino' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
]

const LanguageMenu = ({ locale, onSelect, onClose }: { locale: string; onSelect: (code: string) => void; onClose: () => void }) => {
  const { formatMessage } = useIntl()
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return languages
    return languages.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q)
    )
  }, [query])

  return (
    <div ref={menuRef} className="flex flex-col">
      <div className="p-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={formatMessage({ id: 'navbar.searchLanguage' })}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-colors bg-white"
            aria-label={formatMessage({ id: 'navbar.searchLanguage' })}
          />
        </div>
      </div>
      <div className="overflow-y-auto max-h-72 p-2 grid grid-cols-2 gap-1">
        {filtered.map((lang) => {
          const isActive = locale === lang.code
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => onSelect(lang.code)}
              className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                isActive
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
              }`}
            >
              <span className="truncate" title={lang.name}>{lang.nativeName}</span>
              {isActive && <Check className="h-4 w-4 flex-shrink-0" />}
            </button>
          )
        })}
        {filtered.length === 0 && (
          <div className="col-span-2 py-6 text-center text-sm text-gray-400">—</div>
        )}
      </div>
    </div>
  )
}

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showLangMenu, setShowLangMenu] = useState(false)
  const [showMobileLang, setShowMobileLang] = useState(false)
  const location = useLocation()
  const intl = useIntl()
  const { locale, changeLanguage, getLocalizedPath } = useLanguage()
  const langWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close desktop language menu on outside click
  useEffect(() => {
    if (!showLangMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (langWrapperRef.current && !langWrapperRef.current.contains(e.target as Node)) {
        setShowLangMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showLangMenu])

  // Close all menus on route change
  useEffect(() => {
    setIsOpen(false)
    setShowLangMenu(false)
    setShowMobileLang(false)
  }, [location.pathname])

  const navItems = [
    { 
      name: intl.formatMessage({ id: 'navbar.home' }), 
      path: getLocalizedPath('/') 
    },
    { 
      name: intl.formatMessage({ id: 'navbar.cropper' }), 
      path: getLocalizedPath('/cropper') 
    },
    { 
      name: intl.formatMessage({ id: 'navbar.faq' }), 
      path: getLocalizedPath('/faq') 
    },
    { 
      name: intl.formatMessage({ id: 'navbar.privacy' }), 
      path: getLocalizedPath('/privacy') 
    },
  ]

  const currentLanguage = languages.find((lang) => lang.code === locale)

  const handleLanguageSelect = (code: string) => {
    changeLanguage(code as any)
    setShowLangMenu(false)
    setShowMobileLang(false)
    setIsOpen(false)
  }

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'backdrop-blur-glass shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className="flex items-center space-x-2 group">
            <div className="p-2 bg-primary-600 rounded-lg group-hover:bg-primary-700 transition-colors">
              <Crop className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-display font-bold text-gray-900">
              {intl.formatMessage({ id: 'navbar.brand' })}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"
                    layoutId="navbar-indicator"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            ))}
            
            {/* Language Switcher */}
            <div className="relative" ref={langWrapperRef}>
              <button
                type="button"
                onClick={() => setShowLangMenu((v) => !v)}
                className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
                aria-haspopup="listbox"
                aria-expanded={showLangMenu}
                aria-label={intl.formatMessage({ id: 'navbar.language' })}
              >
                <Globe className="h-4 w-4" />
                <span>{currentLanguage?.nativeName}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${showLangMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 overflow-hidden">
                  <LanguageMenu locale={locale} onSelect={handleLanguageSelect} onClose={() => setShowLangMenu(false)} />
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors"
              aria-label="Menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ 
            opacity: isOpen ? 1 : 0, 
            height: isOpen ? 'auto' : 0 
          }}
          transition={{ duration: 0.2 }}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white rounded-lg shadow-lg mt-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Mobile Language Switcher (collapsible) */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowMobileLang((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700"
                aria-expanded={showMobileLang}
              >
                <span className="flex items-center">
                  <Globe className="h-4 w-4 mr-2" />
                  {intl.formatMessage({ id: 'navbar.language' })}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {currentLanguage?.nativeName}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showMobileLang ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {showMobileLang && (
                <div className="mt-3 px-1 border border-gray-100 rounded-lg overflow-hidden">
                  <LanguageMenu locale={locale} onSelect={handleLanguageSelect} onClose={() => setShowMobileLang(false)} />
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </nav>
  )
}

export default Navbar
